import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Receipt, ChartBar, FileXls } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAYMENT_LABEL = {
  efectivo: 'Efectivo',
  tarjeta: 'Tarjeta',
  transferencia: 'Transferencia',
  mixto: 'Mixto',
  otro: 'Otro',
};

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => {
  const d = new Date(); d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

export const SalesReport = () => {
  const [start, setStart] = useState(daysAgo(7));
  const [end, setEnd] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Lista de cortes disponibles + cuáles están seleccionados (CSV vacío = todos)
  const [cuts, setCuts] = useState([]);
  const [selectedCutIds, setSelectedCutIds] = useState([]);

  // Filtramos cortes por el rango de fechas [start, end] (ambos incluidos, día completo).
  const cutsInRange = useMemo(() => {
    const startBoundary = start ? new Date(`${start}T00:00:00`) : null;
    const endBoundary = end ? new Date(`${end}T23:59:59.999`) : null;
    return cuts.filter((c) => {
      const iso = c.started_at || c.ended_at || c.created_at;
      if (!iso) return true;
      const d = new Date(iso);
      if (startBoundary && d < startBoundary) return false;
      if (endBoundary && d > endBoundary) return false;
      return true;
    });
  }, [cuts, start, end]);

  // Formato "12 jun · 19:22" a partir de un ISO
  const fmtCutDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const mes = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'][d.getMonth()];
      const dd = String(d.getDate()).padStart(2, '0');
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      return `${dd} ${mes} · ${hh}:${mm}`;
    } catch { return iso.slice(0, 16).replace('T', ' '); }
  };

  const buildUrl = (s, e, cuts) => {
    const params = new URLSearchParams({ start: s, end: e });
    if (cuts && cuts.length > 0) params.set('cut_ids', cuts.join(','));
    return `${API}/reports/sales-summary?${params.toString()}`;
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(buildUrl(start, end, selectedCutIds), { withCredentials: true });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al generar reporte');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [reportRes, cutsRes] = await Promise.all([
          axios.get(buildUrl(start, end, []), { withCredentials: true }),
          axios.get(`${API}/cuts`, { withCredentials: true }),
        ]);
        if (!cancelled) {
          setData(reportRes.data);
          // Ordenamos los cortes del más reciente al más viejo
          const list = (cutsRes.data || []).slice().sort((a, b) =>
            (b.started_at || '').localeCompare(a.started_at || '')
          );
          setCuts(list);
        }
      } catch (e) {
        if (!cancelled) {
          toast.error(e.response?.data?.detail || 'Error al generar reporte');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const exportCSV = () => {
    if (!data) {
      return;
    }
    const lines = ['Tipo,Producto,SKU,Cantidad,Ingreso,Precio min,Precio max,Distintos precios'];
    for (const p of data.by_product) {
      const name = (p.product_name || '').replace(/"/g, '""');
      const tipo = p.is_package ? 'Paquete' : 'Producto';
      lines.push(`${tipo},"${name}","${p.sku || ''}",${p.quantity},${p.revenue.toFixed(2)},${p.min_price.toFixed(2)},${p.max_price.toFixed(2)},${p.different_prices ? 'Sí' : 'No'}`);
    }
    lines.push('');
    lines.push('Forma de pago,Monto,Porcentaje');
    for (const m of data.by_payment_method) {
      lines.push(`${PAYMENT_LABEL[m.method] || m.method},${m.amount.toFixed(2)},${m.percent.toFixed(2)}%`);
    }
    lines.push('');
    lines.push(`TOTAL,${data.totals.total_amount.toFixed(2)},${data.totals.sales_count} ventas`);
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reporte-ventas-${start}_${end}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <MainLayout>
      <div className="p-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <ChartBar size={32} weight="bold" className="text-emerald-600" />
            <div>
              <h1 className="text-4xl font-bold tracking-tight">Reporte de Ventas</h1>
              <p className="text-muted-foreground">Resumen entre fechas: productos vendidos + formas de pago</p>
            </div>
          </div>
          {data && data.totals.sales_count > 0 && (
            <Button variant="outline" onClick={exportCSV} data-testid="export-csv-btn">
              <FileXls size={18} className="mr-1" /> Exportar CSV
            </Button>
          )}
        </div>

        {/* Date range */}
        <div className="bg-white border border-border rounded-sm p-4 mb-4 flex flex-wrap items-end gap-3">
          <div>
            <Label>Desde</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-44" data-testid="start-input" />
          </div>
          <div>
            <Label>Hasta</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-44" data-testid="end-input" />
          </div>
          <Button onClick={fetchReport} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="generate-btn">
            {loading ? 'Generando…' : 'Generar reporte'}
          </Button>
          <div className="ml-auto flex gap-2">
            <button onClick={() => { setStart(today()); setEnd(today()); }} className="text-xs px-2 py-1 border rounded hover:bg-slate-100">Hoy</button>
            <button onClick={() => { setStart(daysAgo(7)); setEnd(today()); }} className="text-xs px-2 py-1 border rounded hover:bg-slate-100">7 días</button>
            <button onClick={() => { setStart(daysAgo(30)); setEnd(today()); }} className="text-xs px-2 py-1 border rounded hover:bg-slate-100">30 días</button>
            <button onClick={() => { setStart(daysAgo(90)); setEnd(today()); }} className="text-xs px-2 py-1 border rounded hover:bg-slate-100">90 días</button>
          </div>
        </div>

        {/* Filtro por cortes */}
        <div className="bg-white border border-border rounded-sm p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <Label className="text-sm font-semibold">📋 Cortes incluidos</Label>
              <p className="text-xs text-muted-foreground">
                Si no seleccionas ninguno, se incluyen todos los cortes en el rango de fechas.
                Si seleccionas, el reporte se filtra solo a esos cortes (intersección con las fechas).
              </p>
            </div>
            <div className="flex gap-1 text-xs">
              <button
                type="button"
                onClick={() => setSelectedCutIds(cutsInRange.map((c) => c.id))}
                className="px-2 py-1 border rounded hover:bg-slate-100"
                data-testid="cut-filter-all"
              >
                Todos
              </button>
              <button
                type="button"
                onClick={() => setSelectedCutIds([])}
                className="px-2 py-1 border rounded hover:bg-slate-100"
                data-testid="cut-filter-clear"
              >
                Ninguno
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
            {cuts.length === 0 ? (
              <p className="text-xs text-muted-foreground">No hay cortes registrados aún.</p>
            ) : cutsInRange.length === 0 ? (
              <p className="text-xs text-muted-foreground">No hay cortes en el rango de fechas seleccionado.</p>
            ) : (
              cutsInRange.map((c) => {
                const active = selectedCutIds.includes(c.id);
                const dateLabel = fmtCutDate(c.started_at || c.ended_at || c.created_at);
                const statusBadge = c.status === 'open' ? '🟢' : '🔒';
                const fmt = (n) => `$${(Number(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() =>
                      setSelectedCutIds((prev) =>
                        prev.includes(c.id) ? prev.filter((x) => x !== c.id) : [...prev, c.id]
                      )
                    }
                    className={`text-xs px-2 py-1.5 rounded border transition flex flex-col items-start gap-0.5 ${
                      active
                        ? 'bg-emerald-600 text-white border-emerald-700'
                        : 'bg-white hover:bg-slate-100 border-slate-300'
                    }`}
                    data-testid={`cut-toggle-${c.id}`}
                    title={`${c.folio || c.id} · ${c.sales_count || 0} ventas`}
                  >
                    <span className="font-semibold">{statusBadge} {dateLabel}</span>
                    <span className={active ? 'opacity-90' : 'text-emerald-700 font-semibold'}>
                      💰 {fmt(c.sales_total)}
                    </span>
                    <span className={`text-[10px] ${active ? 'opacity-80' : 'text-amber-700'}`}>
                      🏦 {fmt(c.transfer_total)}
                    </span>
                  </button>
                );
              })
            )}
          </div>
          {selectedCutIds.length > 0 && (
            <p className="text-xs text-emerald-700 mt-2 font-medium">
              ✓ {selectedCutIds.length} corte(s) seleccionado(s) — el reporte se restringirá a esas ventas.
            </p>
          )}
        </div>

        {data && (
          <>
            {/* Totals */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              <div className="bg-emerald-600 text-white rounded-sm p-4">
                <p className="text-xs uppercase tracking-wide opacity-80">💰 Total vendido</p>
                <p className="text-3xl font-bold mt-1" data-testid="total-amount">${data.totals.total_amount.toFixed(2)}</p>
                <p className="text-[11px] opacity-70 mt-1">{data.totals.sales_count} ventas · {data.range.start} a {data.range.end}</p>
              </div>
              <div className="bg-white border border-border rounded-sm p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Piezas vendidas</p>
                <p className="text-3xl font-bold mt-1" data-testid="total-qty">{data.totals.total_qty}</p>
                <p className="text-[11px] text-muted-foreground mt-1">Suma total de cantidades</p>
              </div>
              <div className="bg-white border border-border rounded-sm p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Productos únicos</p>
                <p className="text-3xl font-bold mt-1">{data.by_product.length}</p>
                <p className="text-[11px] text-muted-foreground mt-1">SKUs diferentes</p>
              </div>
            </div>

            {/* Payment methods */}
            <div className="bg-white border border-border rounded-sm p-4 mb-4">
              <h2 className="font-semibold mb-2 flex items-center gap-2"><Receipt size={18} /> Formas de pago</h2>
              {data.by_payment_method.length === 0 ? (
                <p className="text-sm text-muted-foreground">Sin ventas en este rango.</p>
              ) : (
                <div className="space-y-2" data-testid="payment-methods">
                  {data.by_payment_method.map((m) => (
                    <div key={m.method} className="flex items-center gap-3">
                      <span className="w-32 text-sm font-medium">{PAYMENT_LABEL[m.method] || m.method}</span>
                      <div className="flex-1 bg-slate-100 rounded-sm overflow-hidden h-7 relative">
                        <div className="h-full bg-emerald-500" style={{ width: `${m.percent}%` }} />
                        <span className="absolute inset-0 flex items-center px-2 text-xs font-semibold text-slate-800">
                          {m.percent.toFixed(1)}%
                        </span>
                      </div>
                      <span className="w-32 text-right font-bold">${m.amount.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Products table */}
            <div className="bg-white border border-border rounded-sm">
              <div className="p-4 border-b">
                <h2 className="font-semibold">Productos vendidos ({data.by_product.length})</h2>
                <p className="text-xs text-muted-foreground">
                  Consolidado por código — si vendiste el mismo producto a $100 y $105, se suma todo aquí (las columnas &quot;Min&quot; y &quot;Max&quot; muestran el rango).
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="products-table">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase">Producto</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase">SKU</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold uppercase">Cantidad</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold uppercase">Ingreso</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold uppercase">Precio min</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold uppercase">Precio max</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {data.by_product.length === 0 && (
                      <tr><td colSpan="6" className="px-3 py-8 text-center text-muted-foreground">Sin ventas en este rango</td></tr>
                    )}
                    {data.by_product.map((p) => (
                      <tr key={`${p.is_package ? 'pkg' : 'prd'}-${p.product_id}`} className="hover:bg-slate-50">
                        <td className="px-3 py-2 font-medium">
                          {p.is_package && (
                            <span className="inline-block mr-1.5 px-1.5 py-0.5 text-[10px] font-bold bg-purple-100 text-purple-700 rounded" title="Paquete">PKG</span>
                          )}
                          {p.product_name}
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">{p.sku || '—'}</td>
                        <td className="px-3 py-2 text-right font-bold">{p.quantity}</td>
                        <td className="px-3 py-2 text-right font-semibold text-emerald-700">${p.revenue.toFixed(2)}</td>
                        <td className="px-3 py-2 text-right text-xs">${p.min_price.toFixed(2)}</td>
                        <td className="px-3 py-2 text-right text-xs">
                          ${p.max_price.toFixed(2)}
                          {p.different_prices && <span className="ml-1 text-[10px] text-amber-700 font-semibold" title="Vendido a distintos precios">⚠</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default SalesReport;
